/**
 * Label Maker — generates printable product labels for freeze-dried goods.
 *
 * Fills a form → renders a live preview → hits window.print() which prints
 * only the label (the print stylesheet hides everything else).
 *
 * Usable in two contexts:
 *   1. Inline inside the Admin Tool Workstation (embedded prop)
 *   2. Full-page route (future) — the same component, no props
 */

import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { AuthContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Printer, RotateCcw, Tag, Save, BookmarkPlus, Trash2, Copy, Download,
  Sparkles, Loader2, ExternalLink, Ruler, ImageDown, FileImage, FileText,
  Languages,
} from 'lucide-react';
import ToolFeedbackButton from './ToolFeedbackButton';
import NutritionFactsPanel, { NUTRITION_STYLES } from './NutritionFactsPanel';
import LabelAIAssistModal from './LabelAIAssistModal';
import RecipeBuilder from './RecipeBuilder';
import AIProgressIndicator from './AIProgressIndicator';
import ToolDisclaimer from './ToolDisclaimer';
import ToolNavBar from './ToolNavBar';

// Label size presets — width x height in millimeters. Custom sizes fall
// through the picker so operators can match their exact media stock.
const LABEL_SIZES = [
  { value: '2x3',   label: '2 × 3 in',        w:  50.8, h:  76.2 },
  { value: '3x4',   label: '3 × 4 in',        w:  76.2, h: 101.6 },
  { value: '4x6',   label: '4 × 6 in',        w: 101.6, h: 152.4 },
  { value: 'a4',    label: 'A4 (210 × 297 mm)', w: 210,   h: 297   },
  { value: 'letter',label: 'US Letter (8.5 × 11 in)', w: 215.9, h: 279.4 },
  { value: 'custom',label: 'Custom…',         w: 100,   h: 150   },
];

// Parse the free-text nutrition block ("Serving size: 10g\nCalories: 3.2\n…")
// into structured lines. Recognises the ordered set of FDA panel fields so
// the preview can render an FDA-style label instead of a raw <pre>.
// Unknown or unparseable lines fall through as free-text below the panel.
const NF_FIELDS = [
  { key: 'servingSize',       label: 'Serving size',       aliases: ['serving size', 'serving'] },
  { key: 'calories',          label: 'Calories',            aliases: ['calories'], big: true },
  { key: 'totalFat',          label: 'Total Fat',           aliases: ['total fat', 'fat'] },
  { key: 'saturatedFat',      label: 'Saturated Fat',       aliases: ['saturated fat', 'sat fat'], indent: 1 },
  { key: 'transFat',          label: 'Trans Fat',           aliases: ['trans fat'], indent: 1 },
  { key: 'cholesterol',       label: 'Cholesterol',         aliases: ['cholesterol'] },
  { key: 'sodium',            label: 'Sodium',              aliases: ['sodium'] },
  { key: 'totalCarbohydrate', label: 'Total Carbohydrate',  aliases: ['total carbohydrate', 'total carbs', 'carbohydrate', 'carbs'] },
  { key: 'dietaryFiber',      label: 'Dietary Fiber',       aliases: ['dietary fiber', 'fiber'], indent: 1 },
  { key: 'totalSugars',       label: 'Total Sugars',        aliases: ['total sugars', 'sugars'], indent: 1 },
  { key: 'addedSugars',       label: 'Added Sugars',        aliases: ['added sugars'], indent: 2 },
  { key: 'protein',           label: 'Protein',             aliases: ['protein'] },
  { key: 'vitaminD',          label: 'Vitamin D',           aliases: ['vitamin d'] },
  { key: 'calcium',           label: 'Calcium',             aliases: ['calcium'] },
  { key: 'iron',              label: 'Iron',                aliases: ['iron'] },
  { key: 'potassium',         label: 'Potassium',           aliases: ['potassium'] },
];

// Map our internal parseNutrition rows (a legacy text-panel shape) into
// the same NutritionData object the AI endpoint returns, so the styled
// panel can render even when the operator only has typed text.
function nutritionDataFromParsedRows(parsed) {
  if (!parsed || !parsed.rows || parsed.rows.length === 0) return null;
  const get = (key) => {
    const row = parsed.rows.find((r) => r.key === key);
    return row ? row.value : null;
  };
  const num = (raw) => {
    if (raw === null || raw === undefined || raw === '') return null;
    const m = String(raw).match(/-?\d+(?:\.\d+)?/);
    return m ? Number(m[0]) : null;
  };
  return {
    serving_size:    get('servingSize'),
    calories:        get('calories'),
    total_fat_g:     num(get('totalFat')),
    saturated_fat_g: num(get('saturatedFat')),
    trans_fat_g:     num(get('transFat')),
    cholesterol_mg:  num(get('cholesterol')),
    sodium_mg:       num(get('sodium')),
    total_carbs_g:   num(get('totalCarbohydrate')),
    dietary_fiber_g: num(get('dietaryFiber')),
    total_sugars_g:  num(get('totalSugars')),
    added_sugars_g:  num(get('addedSugars')),
    protein_g:       num(get('protein')),
    vitamin_d_mcg:   num(get('vitaminD')),
    calcium_mg:      num(get('calcium')),
    iron_mg:         num(get('iron')),
    potassium_mg:    num(get('potassium')),
  };
}

function parseNutrition(raw) {
  if (!raw || !raw.trim()) return null;
  const rows = [];
  const extra = [];
  for (let line of raw.split(/\r?\n/)) {
    const clean = line.replace(/^\s*[-•*]\s*/, '').trim();
    if (!clean) continue;
    const idx = clean.indexOf(':');
    if (idx < 0) { extra.push(clean); continue; }
    const label = clean.slice(0, idx).trim().toLowerCase().replace(/^\s+/, '');
    const value = clean.slice(idx + 1).trim();
    const matched = NF_FIELDS.find((f) => f.aliases.some((a) => label === a || label.replace(/[^a-z ]/g, '').trim() === a));
    if (matched) rows.push({ ...matched, value });
    else extra.push(clean);
  }
  if (rows.length === 0) return null;
  return { rows, extra };
}

// Extract grams from a free-text serving-size string like "10 g", "2/3 cup
// (55g)", "1.5 oz". Falls through to null when we can't confidently pull a
// weight — the scaler then leaves the panel alone.
function servingSizeToGrams(text) {
  if (!text) return null;
  const s = String(text).toLowerCase();
  const gMatch = s.match(/(\d+(?:\.\d+)?)\s*g\b/);
  if (gMatch) return Number(gMatch[1]);
  const ozMatch = s.match(/(\d+(?:\.\d+)?)\s*oz\b/);
  if (ozMatch) return Number(ozMatch[1]) * 28.3495;
  const lbMatch = s.match(/(\d+(?:\.\d+)?)\s*lb\b/);
  if (lbMatch) return Number(lbMatch[1]) * 453.592;
  return null;
}

// Convert the operator's net-weight input into grams (g/oz/lb accepted).
function netWeightToGrams(value, unit) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  const u = (unit || 'g').toLowerCase();
  if (u === 'g')  return n;
  if (u === 'kg') return n * 1000;
  if (u === 'oz') return n * 28.3495;
  if (u === 'lb') return n * 453.592;
  if (u === 'mg') return n / 1000;
  return n;
}

// Scale numeric nutrition fields to match a new net weight. `baselineG`
// is the grams the AI originally quoted; if we don't have it (operator
// typed the panel by hand), we fall back to the serving_size string
// baked into the current data. When neither is known, the panel is
// returned untouched.
const NUTRITION_NUMERIC_FIELDS = [
  'total_fat_g', 'total_fat_dv', 'saturated_fat_g', 'saturated_fat_dv',
  'trans_fat_g', 'cholesterol_mg', 'cholesterol_dv', 'sodium_mg',
  'sodium_dv', 'total_carbs_g', 'total_carbs_dv', 'dietary_fiber_g',
  'dietary_fiber_dv', 'total_sugars_g', 'added_sugars_g', 'added_sugars_dv',
  'protein_g', 'vitamin_d_mcg', 'vitamin_d_dv', 'calcium_mg', 'calcium_dv',
  'iron_mg', 'iron_dv', 'potassium_mg', 'potassium_dv',
];

function scaleNutritionForNetWeight(data, baselineG, netWeight, netWeightUnit) {
  if (!data) return data;
  const targetG = netWeightToGrams(netWeight, netWeightUnit);
  if (!targetG) return data;
  const originG = baselineG || servingSizeToGrams(data.serving_size);
  if (!originG || Math.abs(originG - targetG) < 0.01) {
    // Nothing to scale — just make sure the serving_size label matches.
    return { ...data, serving_size: `${netWeight}${netWeightUnit}` };
  }
  const ratio = targetG / originG;
  const scaled = { ...data };
  // Scale calories too — it comes in as a string, so parse then reformat.
  if (data.calories !== null && data.calories !== undefined && data.calories !== '') {
    const cMatch = String(data.calories).match(/-?\d+(?:\.\d+)?/);
    if (cMatch) scaled.calories = String(Math.round(Number(cMatch[0]) * ratio));
  }
  for (const k of NUTRITION_NUMERIC_FIELDS) {
    const v = data[k];
    if (v === null || v === undefined || v === '') continue;
    const n = Number(v);
    if (!Number.isFinite(n)) continue;
    // Grams / mg keep one decimal; %DV rounds to integer.
    if (k.endsWith('_dv')) {
      scaled[k] = Math.round(n * ratio);
    } else {
      scaled[k] = Math.round(n * ratio * 10) / 10;
    }
  }
  scaled.serving_size = `${netWeight}${netWeightUnit}`;
  scaled.servings_per_container = '1';
  return scaled;
}

const DEFAULT_FORM = {
  productName: 'Freeze-Dried Strawberries',
  brand: 'Freeze Dry Academy',
  batchNumber: 'FDACADEMY-2026-001',
  freezeDryDate: new Date().toISOString().slice(0, 10),
  bestByMonths: 24,
  netWeight: '40',
  netWeightUnit: 'g',
  ingredients: 'Strawberries',
  allergens: '',
  rehydrationRatio: '1 : 4 (add 4x water by weight, soak 5 min)',
  storage: 'Store sealed in a cool, dry place. Reseal after opening.',
  nutrition: '',
  notes: '',
  logo: '',      // data-URL of an uploaded brand logo, rendered at top of the label
  logoSize: 'medium',  // 'small' | 'medium' | 'large' — controls preview + print height
  warnings: '',  // free-text warnings printed at the bottom of the label
};

// Logo size presets — cap the rendered height on the label preview and
// the PNG/PDF export. Values chosen so the logo never crowds the product
// name at the smallest label size (2×3").
const LOGO_SIZES = {
  small:  { label: 'Small',  maxHeight: '10mm' },
  medium: { label: 'Medium', maxHeight: '18mm' },
  large:  { label: 'Large',  maxHeight: '28mm' },
};

// Chrome i18n — the static labels around the free-text fields.
// Operator-authored strings (ingredients, warnings, storage, etc.) get
// translated server-side via the /label-presets/translate endpoint.
const LABEL_CHROME = {
  en: {
    brand:       'Brand',
    productName: 'Product name',
    netWt:       'Net wt',
    batch:       'Batch',
    ingredients: 'Ingredients',
    rehydrate:   'Rehydrate',
    storage:     'Storage',
    fdDate:      'FD Date',
    bestBy:      'Best by',
    warnings:    'Warnings',
  },
  fr: {
    brand:       'Marque',
    productName: 'Nom du produit',
    netWt:       'Poids net',
    batch:       'Lot',
    ingredients: 'Ingrédients',
    rehydrate:   'Réhydrater',
    storage:     'Conservation',
    fdDate:      'Date de lyophilisation',
    bestBy:      'Meilleur avant',
    warnings:    'Avertissements',
  },
};

// Free-text form fields that get shipped to /label-presets/translate.
// Brand and batch number stay verbatim — those are proper nouns / codes.
const TRANSLATABLE_FIELDS = [
  'productName', 'ingredients', 'allergens', 'rehydrationRatio',
  'storage', 'notes', 'warnings',
];

// A fully-filled reference label so first-time users can see what a
// finished, print-ready label looks like end-to-end. Nutrition values
// reflect the freeze-dried (concentrated) state of fresh strawberries.
const EXAMPLE_STRAWBERRIES = {
  form: {
    productName: 'Freeze-Dried Strawberry Slices',
    brand: 'Freeze Dry Academy',
    batchNumber: 'FDA-STRW-2026-042',
    freezeDryDate: new Date().toISOString().slice(0, 10),
    bestByMonths: 24,
    netWeight: '40',
    netWeightUnit: 'g',
    ingredients: 'Freeze-dried strawberries (100%)',
    allergens: '',
    rehydrationRatio: '1 : 4 by weight (soak 3–5 min in cool water, drain excess)',
    storage: 'Store sealed in a cool, dry place away from direct light. Reseal tightly after opening.',
    nutrition: '',
    notes: 'Freeze-dried at −40 °C to <3% residual moisture. Non-GMO. No added sugar.',
    logo: '',
    warnings: 'Contains sulfites in trace amounts from the source fruit. Keep desiccant packet sealed and out of reach of children.',
  },
  nutritionData: {
    servings_per_container: '4',
    serving_size: '10 g (≈ 1/2 cup)',
    calories: '35',
    total_fat_g: 0.3, total_fat_dv: 0,
    saturated_fat_g: 0, saturated_fat_dv: 0,
    trans_fat_g: 0,
    cholesterol_mg: 0, cholesterol_dv: 0,
    sodium_mg: 1, sodium_dv: 0,
    total_carbs_g: 8.5, total_carbs_dv: 3,
    dietary_fiber_g: 2.2, dietary_fiber_dv: 8,
    total_sugars_g: 5.0,
    added_sugars_g: 0, added_sugars_dv: 0,
    protein_g: 0.7,
    vitamin_d_mcg: 0, vitamin_d_dv: 0,
    calcium_mg: 17, calcium_dv: 1,
    iron_mg: 0.5, iron_dv: 3,
    potassium_mg: 165, potassium_dv: 4,
  },
  sources: [
    'https://fdc.nal.usda.gov/fdc-app.html#/food-details/167762/nutrients',
  ],
};

// Compute a best-by date from the freeze-dry date + months of shelf life.
const computeBestBy = (isoDate, months) => {
  if (!isoDate) return '';
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return '';
  d.setMonth(d.getMonth() + Number(months || 0));
  return d.toISOString().slice(0, 10);
};

export default function LabelMaker({ embedded = false }) {
  const { API, user } = useContext(AuthContext);
  const [form, setForm] = useState(DEFAULT_FORM);
  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  // --- Shared preset library (any signed-in operator can save/load) -------
  const [presets, setPresets] = useState([]);
  const [selectedPresetId, setSelectedPresetId] = useState('');
  const [presetName, setPresetName] = useState('');
  const [savingPreset, setSavingPreset] = useState(false);

  // --- Nutrition-facts lookup --------------------------------------------
  const [nutritionLoading, setNutritionLoading] = useState(false);
  const [nutritionSources, setNutritionSources] = useState([]);
  const [nutritionData, setNutritionData] = useState(null);   // structured JSON from AI

  // Remember the serving size the AI originally quoted the panel for.
  // When the operator changes `form.netWeight`, we scale the panel back
  // from THIS baseline so repeated resizes don't compound rounding.
  const [nutritionBaselineG, setNutritionBaselineG] = useState(null);
  const [nutritionStyle, setNutritionStyle] = useState('fda_us');

  // --- Label size + export ------------------------------------------------
  const [labelSize, setLabelSize] = useState('4x6');
  const [customW, setCustomW]   = useState(100);   // mm
  const [customH, setCustomH]   = useState(150);   // mm
  const [sizeUnit, setSizeUnit] = useState('mm');   // 'mm' | 'in'
  const [exporting, setExporting] = useState(false);
  const previewRef = useRef(null);

  const activeSize = useMemo(() => {
    if (labelSize === 'custom') {
      const w = sizeUnit === 'in' ? customW * 25.4 : Number(customW) || 100;
      const h = sizeUnit === 'in' ? customH * 25.4 : Number(customH) || 150;
      return { w, h };
    }
    const preset = LABEL_SIZES.find((s) => s.value === labelSize) || LABEL_SIZES[2];
    return { w: preset.w, h: preset.h };
  }, [labelSize, customW, customH, sizeUnit]);

  // --- AI Assist modal ---------------------------------------------------
  const [aiModalOpen, setAiModalOpen] = useState(false);

  const applyAIsuggestions = (s) => {
    setForm((prev) => ({
      ...prev,
      productName:      s.productName      || prev.productName,
      ingredients:      s.ingredients      || prev.ingredients,
      allergens:        s.allergens        || prev.allergens,
      rehydrationRatio: s.rehydrationRatio || prev.rehydrationRatio,
      storage:          s.storage          || prev.storage,
      notes:            s.notes            || prev.notes,
      warnings:         s.warnings         || prev.warnings,
    }));
    // If the modal also ran nutrition-lookup, hydrate the panel + sources
    // so the operator gets a fully-populated label in one click.
    if (s.nutritionData) {
      setNutritionData(s.nutritionData);
      setNutritionBaselineG(servingSizeToGrams(s.nutritionData.serving_size));
    }
    if (Array.isArray(s.nutritionSources)) {
      setNutritionSources(s.nutritionSources);
    }
  };

  // --- Recipe Builder ----------------------------------------------------
  const applyRecipeEstimate = ({
    recipe_name, ingredients_ordered, nutritionData, sources, computed, narrative,
  }) => {
    setForm((prev) => ({
      ...prev,
      productName: recipe_name || prev.productName,
      ingredients: ingredients_ordered || prev.ingredients,
      netWeight:  computed?.serving_size_g
        ? String(Math.round(computed.serving_size_g))
        : prev.netWeight,
      notes: narrative
        ? `${narrative}${prev.notes ? `\n\n${prev.notes}` : ''}`.slice(0, 500)
        : prev.notes,
    }));
    setNutritionData(nutritionData || null);
    setNutritionBaselineG(
      computed?.serving_size_g || servingSizeToGrams(nutritionData?.serving_size),
    );
    setNutritionSources(sources || []);
  };

  // --- Label language ---------------------------------------------------
  const [labelLang, setLabelLang] = useState('en');
  const [translating, setTranslating] = useState(false);
  const chrome = LABEL_CHROME[labelLang] || LABEL_CHROME.en;

  // When the label switches to French, auto-flip the nutrition style to
  // the Canadian French variant so the whole label reads in one voice.
  // Only nudge in one direction — flipping back to English doesn't undo
  // an intentional mini/simplified pick.
  useEffect(() => {
    if (labelLang === 'fr' && nutritionStyle === 'fda_us') {
      setNutritionStyle('ca_bilingual');
    }
  }, [labelLang]); // eslint-disable-line

  const translateLabel = async (target) => {
    // Guard: if we're already in the target lang, don't fire the AI call.
    if (target === labelLang) { setLabelLang(target); return; }
    // Nothing to translate? Just flip the chrome.
    const payloadFields = {};
    for (const k of TRANSLATABLE_FIELDS) {
      if (form[k] && form[k].trim()) payloadFields[k] = form[k];
    }
    if (Object.keys(payloadFields).length === 0) {
      setLabelLang(target);
      return;
    }
    setTranslating(true);
    try {
      const { data } = await axios.post(
        `${API}/label-presets/translate`,
        { target_lang: target, fields: payloadFields },
      );
      setForm((prev) => ({ ...prev, ...data }));
      setLabelLang(target);
      toast.success(target === 'fr' ? 'Étiquette traduite en français.' : 'Label translated to English.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Translation failed');
    } finally { setTranslating(false); }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please pick an image file (PNG, JPEG, SVG).');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo is larger than 2 MB — please compress before upload.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setField('logo', String(reader.result || ''));
    reader.onerror = () => toast.error('Could not read the logo file.');
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // --- Example label — one-click demo so first-time users see a finished
  // label end-to-end (including a populated FDA Nutrition Facts panel).
  const loadExample = () => {
    setForm((prev) => ({ ...prev, ...EXAMPLE_STRAWBERRIES.form }));
    setNutritionData(EXAMPLE_STRAWBERRIES.nutritionData);
    setNutritionBaselineG(servingSizeToGrams(EXAMPLE_STRAWBERRIES.nutritionData.serving_size));
    setNutritionSources(EXAMPLE_STRAWBERRIES.sources);
    setNutritionStyle('fda_us');
    setLabelSize('4x6');
    toast.success('Loaded the Strawberry example — tweak or print as-is.');
  };

  const bestBy = useMemo(
    () => computeBestBy(form.freezeDryDate, form.bestByMonths),
    [form.freezeDryDate, form.bestByMonths],
  );

  const loadPresets = async () => {
    if (!API) return;
    try {
      const { data } = await axios.get(`${API}/label-presets`);
      setPresets(Array.isArray(data) ? data : []);
    } catch (_e) { /* silent — presets are optional */ }
  };

  useEffect(() => { loadPresets(); /* eslint-disable-next-line */ }, [API]);

  const applyPreset = (id) => {
    if (!id) { setSelectedPresetId(''); return; }
    const p = presets.find((x) => x.id === id);
    if (!p) return;
    const payload = p.payload || {};
    // Split off the "extras" the operator stashed alongside the form —
    // nutrition data, cited sources, chosen panel style, label size.
    // Anything missing resets to a sensible default so the previous
    // preset's panel never bleeds into this one.
    setForm((prev) => ({
      ...DEFAULT_FORM,
      ...payload,
      // Never let stashed extras leak into the form as spurious fields.
      _nutritionData:    undefined,
      _nutritionSources: undefined,
      _nutritionStyle:   undefined,
      _labelSize:        undefined,
    }));
    setNutritionData(payload._nutritionData || null);
    setNutritionBaselineG(
      payload._nutritionBaselineG || servingSizeToGrams(payload._nutritionData?.serving_size),
    );
    setNutritionSources(payload._nutritionSources || []);
    setNutritionStyle(payload._nutritionStyle || 'fda_us');
    if (payload._labelSize) setLabelSize(payload._labelSize);
    setLabelLang(payload._labelLang || 'en');
    toast.success(`Loaded preset "${p.name}"`);
  };

  const savePreset = async () => {
    const name = presetName.trim();
    if (!name) { toast.error('Give the preset a name first.'); return; }
    setSavingPreset(true);
    try {
      const { data } = await axios.post(`${API}/label-presets`, {
        name,
        payload: {
          productName:      form.productName,
          brand:            form.brand,
          netWeight:        form.netWeight,
          netWeightUnit:    form.netWeightUnit,
          bestByMonths:     form.bestByMonths,
          ingredients:      form.ingredients,
          allergens:        form.allergens,
          rehydrationRatio: form.rehydrationRatio,
          storage:          form.storage,
          nutrition:        form.nutrition,
          notes:            form.notes,
          logo:             form.logo,
          logoSize:         form.logoSize,
          warnings:         form.warnings,
          // Persist the AI-fetched nutrition panel + cited sources +
          // chosen style + label size + language so loading this preset
          // later restores the full label, not just the form fields.
          _nutritionData:    nutritionData,
          _nutritionSources: nutritionSources,
          _nutritionBaselineG: nutritionBaselineG,
          _nutritionStyle:   nutritionStyle,
          _labelSize:        labelSize,
          _labelLang:        labelLang,
        },
      });
      setPresets((prev) => [data, ...prev]);
      setSelectedPresetId(data.id);
      setPresetName('');
      toast.success(`Saved preset "${data.name}"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSavingPreset(false); }
  };

  const deletePreset = async () => {
    if (!selectedPresetId) return;
    const p = presets.find((x) => x.id === selectedPresetId);
    if (!p) return;
    if (!window.confirm(`Delete preset "${p.name}"?`)) return;
    try {
      await axios.delete(`${API}/label-presets/${selectedPresetId}`);
      setPresets((prev) => prev.filter((x) => x.id !== selectedPresetId));
      setSelectedPresetId('');
      toast.success('Preset deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const duplicatePreset = async () => {
    if (!selectedPresetId) { toast.error('Pick a preset to duplicate first.'); return; }
    const src = presets.find((x) => x.id === selectedPresetId);
    if (!src) return;
    const suggested = `${src.name} (copy)`.slice(0, 118);
    const newName = window.prompt('Name your copy', suggested);
    if (!newName || !newName.trim()) return;
    setSavingPreset(true);
    try {
      const { data } = await axios.post(`${API}/label-presets`, {
        name: newName.trim(),
        payload: src.payload || {},
      });
      setPresets((prev) => [data, ...prev]);
      setSelectedPresetId(data.id);
      // Also load into the form so the operator can tweak immediately.
      setForm((prev) => ({ ...prev, ...(data.payload || {}) }));
      toast.success(`Duplicated as "${data.name}" — edit and re-save when ready.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Duplicate failed');
    } finally { setSavingPreset(false); }
  };

  const lookupNutrition = async () => {
    const q = (form.productName || form.ingredients || '').trim();
    if (!q) { toast.error('Enter a product name or ingredient first.'); return; }
    setNutritionLoading(true);
    setNutritionSources([]);
    try {
      const { data } = await axios.post(
        `${API}/label-presets/nutrition-lookup`,
        {
          query: q,
          serving_size: form.netWeight ? `${form.netWeight}${form.netWeightUnit}` : null,
          freeze_dried: true,
        },
      );
      const disc = data.disclaimer ? `\n\n${data.disclaimer}` : '';
      setField('nutrition', `${data.nutrition || ''}${disc}`);
      setNutritionSources(data.sources || []);
      setNutritionData(data.data || null);
      setNutritionBaselineG(servingSizeToGrams(data.data?.serving_size));
      if (!data.nutrition && !data.data) toast.warning('No values returned — try a more specific query.');
      else toast.success('Nutrition facts loaded — verify sources before printing.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Nutrition lookup failed');
    } finally { setNutritionLoading(false); }
  };

  // ---------- Export: PNG / JPEG / PDF (client-side) --------------------
  const captureCanvas = async () => {
    if (!previewRef.current) return null;
    // Scale 3× for print-ready DPI (~228 dpi at CSS 96 dpi baseline).
    return html2canvas(previewRef.current, {
      background: '#ffffff',
      scale: 3,
      useCORS: true,
    });
  };

  const download = (dataUrl, filename) => {
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const safeName = () =>
    (form.productName || 'label')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 60) || 'label';

  const exportImage = async (mime) => {
    setExporting(true);
    try {
      const canvas = await captureCanvas();
      if (!canvas) return;
      const ext = mime === 'image/jpeg' ? 'jpg' : 'png';
      download(canvas.toDataURL(mime, 0.95), `${safeName()}.${ext}`);
      toast.success(`Downloaded ${ext.toUpperCase()}`);
    } catch (e) {
      toast.error(`Export failed: ${e.message || e}`);
    } finally { setExporting(false); }
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const canvas = await captureCanvas();
      if (!canvas) return;
      const { w, h } = activeSize;
      const orientation = w > h ? 'landscape' : 'portrait';
      const pdf = new jsPDF({ orientation, unit: 'mm', format: [w, h] });
      const img = canvas.toDataURL('image/png');
      pdf.addImage(img, 'PNG', 0, 0, w, h, undefined, 'FAST');
      pdf.save(`${safeName()}.pdf`);
      toast.success('Downloaded PDF');
    } catch (e) {
      toast.error(`PDF export failed: ${e.message || e}`);
    } finally { setExporting(false); }
  };

  const handlePrint = () => {
    document.body.classList.add('label-maker-printing');
    window.print();
    // Cleanup happens on any of onafterprint, focus, or timeout.
    const cleanup = () => document.body.classList.remove('label-maker-printing');
    window.onafterprint = cleanup;
    setTimeout(cleanup, 1500);
  };

  const reset = () => setForm(DEFAULT_FORM);

  return (
    <div className={embedded ? '' : 'max-w-6xl mx-auto p-6'} data-testid="label-maker-root">
      {!embedded && <ToolNavBar activeSlug="label-maker" />}
      {/* Header with feedback CTA — tucked in a corner so it doesn't
          clutter the tool but is always reachable while testing. */}
      <div className="flex justify-end mb-3 no-print gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={loadExample}
          data-testid="lm-load-example"
          title="Fill the entire form with a print-ready strawberry label so you can see what one looks like"
        >
          <Tag className="w-4 h-4 mr-2" /> Try example: Strawberries
        </Button>
        {user && (
          <Button
            type="button"
            size="sm"
            onClick={() => setAiModalOpen(true)}
            data-testid="lm-ai-assist-open"
            title="Fills every label field — copy AND nutrition — from just a product name"
          >
            <Sparkles className="w-4 h-4 mr-2" /> Master AI Fill
          </Button>
        )}
        <ToolFeedbackButton toolSlug="label-maker" label="Send feedback" />
      </div>

      <ToolDisclaimer
        testid="lm-disclaimer"
        intro={
          <>
            <p className="font-semibold text-foreground">What this tool does</p>
            <p>
              The <span className="font-semibold">Label Maker</span> builds a
              print-ready product label for freeze-dried goods. Save shared
              presets, use <span className="font-semibold">Master AI Fill</span>{' '}
              to draft copy + Nutrition Facts from just a product name, pick
              from four regulatory panel styles (US FDA / Canadian EN & FR /
              Mini), size the label in mm or inches, translate the whole label
              to French, and export as PNG / JPEG / PDF. Use{' '}
              <span className="font-semibold">Recipe Mode</span> to combine
              wet-weight ingredients into a finished freeze-dried nutrition
              panel.
            </p>
          </>
        }
      />

      {/* --------- Preset toolbar (shared library) --------- */}
      {user && (
        <Card className="no-print mb-4" data-testid="lm-preset-toolbar">
          <CardContent className="p-4 flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[220px]">
              <Label className="text-xs uppercase tracking-wider">Load a shared preset</Label>
              <select
                value={selectedPresetId}
                onChange={(e) => setSelectedPresetId(e.target.value)}
                className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                data-testid="lm-preset-select"
              >
                <option value="">— Pick a preset —</option>
                {presets.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                    {p.created_by_email ? ` · by ${p.created_by_email}` : ''}
                  </option>
                ))}
              </select>
            </div>
            <Button
              type="button"
              onClick={() => applyPreset(selectedPresetId)}
              disabled={!selectedPresetId}
              data-testid="lm-preset-load"
              title="Fill the form + nutrition panel from the selected preset"
            >
              <Download className="w-4 h-4 mr-2" /> Load
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={duplicatePreset}
              disabled={!selectedPresetId || savingPreset}
              data-testid="lm-preset-duplicate"
              title="Clone the selected preset into a new copy so you can tweak it without overwriting the original"
            >
              <Copy className="w-4 h-4 mr-2" /> Duplicate
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={deletePreset}
              disabled={!selectedPresetId || !(presets.find((p) => p.id === selectedPresetId) || {}).is_mine}
              data-testid="lm-preset-delete"
              title="Only the preset's owner (or an admin) can delete it"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Delete
            </Button>
            <div className="w-px h-10 bg-border" />
            <div className="flex-1 min-w-[220px]">
              <Label className="text-xs uppercase tracking-wider">Save current form as preset</Label>
              <Input
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                placeholder='e.g. "Beef Jerky — House Standard"'
                data-testid="lm-preset-name"
              />
            </div>
            <Button
              type="button"
              onClick={savePreset}
              disabled={savingPreset}
              data-testid="lm-preset-save"
            >
              {savingPreset
                ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                : <BookmarkPlus className="w-4 h-4 mr-2" />}
              Save preset
            </Button>
          </CardContent>
        </Card>
      )}

      {/* --------- Recipe Mode (inline collapsible) --------- */}
      {user && <RecipeBuilder onEstimate={applyRecipeEstimate} />}

      {/* Print styles: hide everything except the label when printing. */}
      <style>{`
        @media print {
          body.label-maker-printing * { visibility: hidden !important; }
          body.label-maker-printing #label-print-area,
          body.label-maker-printing #label-print-area * { visibility: visible !important; }
          body.label-maker-printing #label-print-area {
            position: absolute; top: 0; left: 0; width: 100%;
            padding: 0; margin: 0; background: #fff !important; color: #000 !important;
          }
          body.label-maker-printing #label-print-area .label-card {
            box-shadow: none !important; border: 1px solid #000 !important;
            background: #fff !important; color: #000 !important;
          }
        }
      `}</style>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* --------- Form ---------- */}
        <Card data-testid="label-maker-form">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Tag className="w-5 h-5 text-primary" /> Label details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Product name</Label>
                <Input
                  value={form.productName}
                  onChange={(e) => setField('productName', e.target.value)}
                  data-testid="lm-product-name"
                />
              </div>
              <div>
                <Label>Brand</Label>
                <Input
                  value={form.brand}
                  onChange={(e) => setField('brand', e.target.value)}
                  data-testid="lm-brand"
                />
              </div>
              <div>
                <Label>Batch / Lot #</Label>
                <Input
                  value={form.batchNumber}
                  onChange={(e) => setField('batchNumber', e.target.value)}
                  data-testid="lm-batch"
                />
              </div>
              <div>
                <Label>Freeze-dry date</Label>
                <Input
                  type="date"
                  value={form.freezeDryDate}
                  onChange={(e) => setField('freezeDryDate', e.target.value)}
                  data-testid="lm-fd-date"
                />
              </div>
              <div>
                <Label>Best-by (months)</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.bestByMonths}
                  onChange={(e) => setField('bestByMonths', e.target.value)}
                  data-testid="lm-best-by-months"
                />
              </div>
              <div>
                <Label>Net weight</Label>
                <Input
                  value={form.netWeight}
                  onChange={(e) => setField('netWeight', e.target.value)}
                  data-testid="lm-weight"
                />
              </div>
              <div>
                <Label>Unit</Label>
                <select
                  value={form.netWeightUnit}
                  onChange={(e) => setField('netWeightUnit', e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  data-testid="lm-weight-unit"
                >
                  <option value="g">g</option>
                  <option value="oz">oz</option>
                  <option value="kg">kg</option>
                  <option value="lb">lb</option>
                </select>
              </div>
            </div>

            <div>
              <Label>Ingredients</Label>
              <Textarea
                rows={2}
                value={form.ingredients}
                onChange={(e) => setField('ingredients', e.target.value)}
                placeholder="Comma-separated, order by weight (heaviest first)"
                data-testid="lm-ingredients"
              />
            </div>

            <div>
              <Label>Allergens (optional)</Label>
              <Input
                value={form.allergens}
                onChange={(e) => setField('allergens', e.target.value)}
                placeholder="Contains: milk, tree nuts…"
                data-testid="lm-allergens"
              />
            </div>

            <div>
              <Label>Rehydration</Label>
              <Input
                value={form.rehydrationRatio}
                onChange={(e) => setField('rehydrationRatio', e.target.value)}
                data-testid="lm-rehydration"
              />
            </div>

            <div>
              <Label>Storage</Label>
              <Input
                value={form.storage}
                onChange={(e) => setField('storage', e.target.value)}
                data-testid="lm-storage"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>Nutrition (optional)</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={lookupNutrition}
                  disabled={nutritionLoading}
                  data-testid="lm-nutrition-lookup"
                  title="Fetch cited FDA/USDA nutrition facts for the product name"
                >
                  {nutritionLoading
                    ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
                  Lookup FDA/USDA
                </Button>
              </div>
              <Textarea
                rows={5}
                value={form.nutrition}
                onChange={(e) => setField('nutrition', e.target.value)}
                placeholder="Per 10g serving — Calories, Protein, Carbs, Fat…"
                data-testid="lm-nutrition"
              />
              <div className="mt-3 flex flex-wrap items-end gap-2">
                <div className="flex-1 min-w-[180px]">
                  <Label className="text-xs">Nutrition label style</Label>
                  <select
                    value={nutritionStyle}
                    onChange={(e) => setNutritionStyle(e.target.value)}
                    className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                    data-testid="lm-nutrition-style"
                  >
                    {NUTRITION_STYLES.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <AIProgressIndicator
                active={nutritionLoading}
                estimatedMs={18000}
                className="mt-3"
                data-testid="lm-nutrition-progress"
                steps={[
                  { at: 0,     label: 'Looking up FDA / USDA nutrition values…' },
                  { at: 5000,  label: 'Applying freeze-dry moisture concentration…' },
                  { at: 12000, label: 'Formatting panel & collecting sources…' },
                ]}
              />
              {nutritionSources.length > 0 && (
                <div
                  className="mt-2 text-xs text-muted-foreground space-y-1"
                  data-testid="lm-nutrition-sources"
                >
                  <div className="font-semibold uppercase tracking-wider">Cited sources:</div>
                  {nutritionSources.map((u) => (
                    <a
                      key={u}
                      href={u}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-primary hover:underline break-all"
                    >
                      <ExternalLink className="w-3 h-3 shrink-0" /> {u}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div>
              <Label>Notes (small text under label)</Label>
              <Input
                value={form.notes}
                onChange={(e) => setField('notes', e.target.value)}
                data-testid="lm-notes"
              />
            </div>

            <div>
              <Label>Warnings (printed at the bottom of the label)</Label>
              <Textarea
                rows={2}
                value={form.warnings}
                onChange={(e) => setField('warnings', e.target.value)}
                placeholder="e.g. Contains sulfites. Keep desiccant packet out of reach of children."
                data-testid="lm-warnings"
              />
            </div>

            <div>
              <Label>Brand logo (optional, near top of label)</Label>
              <div className="flex items-center gap-3 mt-1 flex-wrap">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="text-sm"
                  data-testid="lm-logo-upload"
                />
                {form.logo && (
                  <>
                    <img
                      src={form.logo}
                      alt="Brand logo preview"
                      className="h-10 max-w-[120px] object-contain border border-border rounded p-1 bg-white"
                      data-testid="lm-logo-thumb"
                    />
                    <div className="flex items-center gap-1">
                      <Label className="text-xs mr-1">Size</Label>
                      <select
                        value={form.logoSize || 'medium'}
                        onChange={(e) => setField('logoSize', e.target.value)}
                        className="h-9 px-2 rounded-md border border-input bg-background text-sm"
                        data-testid="lm-logo-size"
                      >
                        {Object.entries(LOGO_SIZES).map(([k, v]) => (
                          <option key={k} value={k}>{v.label}</option>
                        ))}
                      </select>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setField('logo', '')}
                      data-testid="lm-logo-clear"
                    >
                      Remove
                    </Button>
                  </>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                PNG, JPEG or SVG — under 2 MB. Rendered above the product name on the label.
              </p>
            </div>

            <div className="flex flex-wrap gap-2 pt-2">
              <Button onClick={handlePrint} data-testid="lm-print">
                <Printer className="w-4 h-4 mr-2" /> Print label
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => exportImage('image/png')}
                disabled={exporting}
                data-testid="lm-export-png"
              >
                {exporting
                  ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  : <ImageDown className="w-4 h-4 mr-2" />}
                Export PNG
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => exportImage('image/jpeg')}
                disabled={exporting}
                data-testid="lm-export-jpeg"
              >
                <FileImage className="w-4 h-4 mr-2" /> Export JPEG
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={exportPdf}
                disabled={exporting}
                data-testid="lm-export-pdf"
              >
                <FileText className="w-4 h-4 mr-2" /> Export PDF
              </Button>
              <Button variant="outline" onClick={reset} data-testid="lm-reset">
                <RotateCcw className="w-4 h-4 mr-2" /> Reset
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* --------- Preview ---------- */}
        <div id="label-print-area">
          {/* Size + style controls above the sized preview so the operator
              can see immediately how content re-flows for their media stock. */}
          <Card className="no-print mb-3" data-testid="lm-size-toolbar">
            <CardContent className="p-3 flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[180px]">
                <Label className="text-xs flex items-center gap-1">
                  <Ruler className="w-3.5 h-3.5" /> Label size
                </Label>
                <select
                  value={labelSize}
                  onChange={(e) => setLabelSize(e.target.value)}
                  className="mt-1 w-full h-9 px-2 rounded-md border border-input bg-background text-sm"
                  data-testid="lm-label-size"
                >
                  {LABEL_SIZES.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
              {labelSize === 'custom' && (
                <>
                  <div className="w-20">
                    <Label className="text-xs">Width</Label>
                    <Input
                      type="number"
                      min="10"
                      value={customW}
                      onChange={(e) => setCustomW(Number(e.target.value))}
                      className="h-9"
                      data-testid="lm-custom-w"
                    />
                  </div>
                  <div className="w-20">
                    <Label className="text-xs">Height</Label>
                    <Input
                      type="number"
                      min="10"
                      value={customH}
                      onChange={(e) => setCustomH(Number(e.target.value))}
                      className="h-9"
                      data-testid="lm-custom-h"
                    />
                  </div>
                  <div className="w-20">
                    <Label className="text-xs">Unit</Label>
                    <select
                      value={sizeUnit}
                      onChange={(e) => setSizeUnit(e.target.value)}
                      className="mt-1 w-full h-9 px-2 rounded-md border border-input bg-background text-sm"
                      data-testid="lm-custom-unit"
                    >
                      <option value="mm">mm</option>
                      <option value="in">inch</option>
                    </select>
                  </div>
                </>
              )}
              <div className="text-xs text-muted-foreground pb-1">
                {activeSize.w.toFixed(1)} × {activeSize.h.toFixed(1)} mm
              </div>
              <div className="ml-auto flex items-end gap-1">
                <Label className="text-xs mr-1 flex items-center gap-1">
                  <Languages className="w-3.5 h-3.5" /> Language
                </Label>
                <div className="inline-flex rounded-md border border-input overflow-hidden" data-testid="lm-lang-toggle">
                  <button
                    type="button"
                    onClick={() => translateLabel('en')}
                    disabled={translating}
                    className={`h-9 px-3 text-sm font-semibold ${labelLang === 'en' ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-accent'}`}
                    data-testid="lm-lang-en"
                  >
                    EN
                  </button>
                  <button
                    type="button"
                    onClick={() => translateLabel('fr')}
                    disabled={translating}
                    className={`h-9 px-3 text-sm font-semibold border-l border-input ${labelLang === 'fr' ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-accent'}`}
                    data-testid="lm-lang-fr"
                  >
                    {translating && labelLang !== 'fr'
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : 'FR'}
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="label-card" data-testid="lm-preview">
            <CardContent
              ref={previewRef}
              className="p-4 space-y-3"
              style={{
                width: `${activeSize.w}mm`,
                minHeight: `${activeSize.h}mm`,
                maxWidth: '100%',
                background: '#ffffff',
                color: '#000',
                boxSizing: 'border-box',
              }}
            >
              {form.logo && (
                <div className="flex justify-center pb-1" data-testid="lm-preview-logo">
                  <img
                    src={form.logo}
                    alt=""
                    style={{
                      maxHeight: (LOGO_SIZES[form.logoSize] || LOGO_SIZES.medium).maxHeight,
                      maxWidth: '80%',
                      objectFit: 'contain',
                    }}
                  />
                </div>
              )}
              <div className="text-center text-[11px] uppercase tracking-widest text-slate-500 font-bold">
                {form.brand || chrome.brand}
              </div>
              <div className="text-center text-2xl font-extrabold leading-tight">
                {form.productName || chrome.productName}
              </div>
              <div className="text-center text-sm text-slate-600 pb-2 border-b border-slate-300">
                <span className="font-bold">{chrome.netWt}:</span> {form.netWeight || '—'} {form.netWeightUnit}
                {' · '}
                <span className="font-bold">{chrome.batch}:</span> {form.batchNumber || '—'}
              </div>

              <div className="pt-3 text-sm space-y-2">
                <div>
                  <span className="font-bold uppercase text-[11px] tracking-wider">{chrome.ingredients}: </span>
                  {form.ingredients || '—'}
                </div>
                {form.allergens ? (
                  <div className="text-red-700 font-semibold">{form.allergens}</div>
                ) : null}
                <div>
                  <span className="font-bold uppercase text-[11px] tracking-wider">{chrome.rehydrate}: </span>
                  {form.rehydrationRatio}
                </div>
                <div>
                  <span className="font-bold uppercase text-[11px] tracking-wider">{chrome.storage}: </span>
                  {form.storage}
                </div>
                <div className="flex justify-between text-xs text-slate-600 pt-1">
                  <span>
                    <span className="font-bold">{chrome.fdDate}:</span> {form.freezeDryDate || '—'}
                  </span>
                  <span>
                    <span className="font-bold">{chrome.bestBy}:</span> {bestBy || '—'}
                  </span>
                </div>
              </div>

              {/* AI-driven Nutrition Facts panel — renders whenever we
                  have EITHER structured nutrition data OR parseable text
                  the operator typed. Both paths flow through the same
                  styled panel so the style dropdown always applies.
                  `key={nutritionStyle}` forces a clean re-mount when the
                  operator swaps styles so the DOM visibly repaints. */}
              {(() => {
                let panelData = nutritionData;
                if (!panelData && form.nutrition) {
                  panelData = nutritionDataFromParsedRows(parseNutrition(form.nutrition));
                }
                if (!panelData) return null;
                panelData = scaleNutritionForNetWeight(
                  panelData, nutritionBaselineG, form.netWeight, form.netWeightUnit,
                );
                return (
                  <div className="mt-3" data-testid="lm-nutrition-panel">
                    <NutritionFactsPanel
                      key={nutritionStyle}
                      data={panelData}
                      style={nutritionStyle}
                      sources={nutritionSources}
                      lang={labelLang}
                    />
                    <div
                      className="text-[9px] uppercase tracking-widest text-slate-500 mt-1 no-print"
                      data-testid="lm-nutrition-style-current"
                    >
                      Style: {(NUTRITION_STYLES.find((s) => s.value === nutritionStyle) || {}).label || nutritionStyle}
                    </div>
                  </div>
                );
              })()}

              {form.notes ? (
                <div className="text-[10px] text-slate-500 border-t border-slate-200 pt-2">
                  {form.notes}
                </div>
              ) : null}

              {form.warnings ? (
                <div
                  className="text-[10px] font-semibold text-red-700 border-2 border-red-700 rounded px-2 py-1 mt-2 whitespace-pre-wrap"
                  data-testid="lm-preview-warnings"
                >
                  <span className="uppercase tracking-wider text-[9px] block mb-0.5">
                    {chrome.warnings}
                  </span>
                  {form.warnings}
                </div>
              ) : null}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* --------- AI Assist Modal --------- */}
      <LabelAIAssistModal
        open={aiModalOpen}
        onClose={() => setAiModalOpen(false)}
        onApply={applyAIsuggestions}
        defaultProduct={form.productName}
      />
    </div>
  );
}
